import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Download, FileText, BookOpen, Calculator, Heart, Gamepad2, Mail, Check, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODULES = [
  {
    icon: BookOpen,
    name: 'StoryPath',
    tagline: 'Stories that travel with the learner.',
    detail: "Narrative literacy booklets built around grade-appropriate stories with guided reading prompts, text-dependent questions, and vocabulary anchors. Designed to pause and resume cleanly so students keep momentum through school changes, absences, or schedule disruptions — every booklet has a self-contained arc with a clear checkpoint at the end.",
    accent: 'from-blue-50 to-white',
    ring: 'ring-blue-100',
    iconBg: 'bg-blue-100 text-blue-700',
  },
  {
    icon: Calculator,
    name: 'LifeMath',
    tagline: 'Math for the way life actually happens.',
    detail: "Applied-math scenario card deck grounded in real lived experience: public transit reading, budgeting and change-making, telling and estimating time, measuring, and unit conversion. Each card pairs a short story context with a targeted skill prompt — built for stations, warm-ups, small-group rotation, or independent practice.",
    accent: 'from-lime-50 to-white',
    ring: 'ring-lime-100',
    iconBg: 'bg-lime-100 text-lime-700',
  },
  {
    icon: Heart,
    name: 'CalmFocus',
    tagline: 'A daily reset for student brains.',
    detail: "Daily trauma-informed social-emotional micro-lessons aligned to SAMHSA Trauma-Informed Care Principles and the CASEL 5 competencies (self-awareness, self-management, social awareness, relationship skills, responsible decision-making). Short, low-stakes entries help students self-regulate, name feelings, and return to learning — no screen required, fully print-based.",
    accent: 'from-pink-50 to-white',
    ring: 'ring-pink-100',
    iconBg: 'bg-pink-100 text-pink-700',
  },
  {
    icon: Gamepad2,
    name: 'Offline Learning Quest',
    tagline: 'Learning that feels like a game — and needs no Wi-Fi.',
    detail: "Gamified cross-content workbook that unifies literacy, math, and SEL into a single progression-tracked journey. Students earn stamps, unlock chapters, and complete challenges entirely on paper — no internet, no device, no power needed. Keeps learners engaged during transitions and in low-connectivity environments.",
    accent: 'from-violet-50 to-white',
    ring: 'ring-violet-100',
    iconBg: 'bg-violet-100 text-violet-700',
  },
  {
    icon: Mail,
    name: 'TalkBridge Student Reply Sheets',
    tagline: 'Families talk back — in their first language.',
    detail: "Bilingual home-to-school reply sheets that close the family communication loop in the top NYCPS household languages (Spanish, Chinese, Bengali, Arabic, Haitian Creole, Russian, Urdu, French, Korean, English). Students bring them home, families respond in their first language, teachers receive structured feedback — satisfies Chancellor's Regulation A-663 language-access expectations.",
    accent: 'from-amber-50 to-white',
    ring: 'ring-amber-100',
    iconBg: 'bg-amber-100 text-amber-700',
  },
];

const GRADE_SCALE = [
  { band: 'K–2 (Early)', reading: 'Pre-reader → Early fluent', themes: 'Belonging, Family, Feelings, Routines' },
  { band: '3–5 (Core)', reading: 'Developing → Proficient', themes: 'Identity, Community, Change, Resilience' },
  { band: '6–8 (Upper)', reading: 'Proficient → Advanced', themes: 'Advocacy, Rights, Careers, Transitions' },
];

const LANGUAGES = [
  ['Spanish', 'Español'], ['Chinese', '中文'], ['Bengali', 'বাংলা'],
  ['Arabic', 'العربية'], ['Haitian Creole', 'Kreyòl Ayisyen'], ['Russian', 'Русский'],
  ['Urdu', 'اردو'], ['French', 'Français'], ['Korean', '한국어'], ['English', 'English'],
];

const GRADE_SKUS = [
  { sku: 'MOS-SSE-STU-K', grade: 'Kindergarten' },
  { sku: 'MOS-SSE-STU-G1', grade: 'Grade 1' },
  { sku: 'MOS-SSE-STU-G2', grade: 'Grade 2' },
  { sku: 'MOS-SSE-STU-G3', grade: 'Grade 3' },
  { sku: 'MOS-SSE-STU-G4', grade: 'Grade 4' },
  { sku: 'MOS-SSE-STU-G5', grade: 'Grade 5' },
  { sku: 'MOS-SSE-STU-G6', grade: 'Grade 6' },
  { sku: 'MOS-SSE-STU-G7', grade: 'Grade 7' },
  { sku: 'MOS-SSE-STU-G8', grade: 'Grade 8' },
];

const useSEO = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'MOSAIC Student Stability & Engagement — At-Home Learning Kits (K-8) | Lerno Co.';

    const meta = (name, content, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return el;
    };

    const desc = 'Physical at-home learning kits for all K-8 learners. StoryPath literacy, LifeMath applied math, CalmFocus SEL, Offline Learning Quest, and TalkBridge bilingual family packets. 100% offline. $149 per student, one SKU per grade. A MOSAIC Program by Lerno Co. NYC Vendor LER599869.';
    const added = [
      meta('description', desc),
      meta('og:title', 'MOSAIC Student Stability & Engagement — At-Home Learning Kits (K-8)', 'property'),
      meta('og:description', desc, 'property'),
      meta('og:type', 'product', 'property'),
      meta('og:url', `${window.location.origin}/stability-kits`, 'property'),
      meta('twitter:card', 'summary_large_image'),
      meta('twitter:title', 'MOSAIC Student Stability & Engagement Kits'),
      meta('twitter:description', desc),
    ];

    return () => {
      document.title = prevTitle;
      added.forEach((el) => el && el.remove());
    };
  }, []);
};

const StabilityKits = () => {
  useSEO();
  const [pdfUrls, setPdfUrls] = useState({
    letter: '/api/blm-downloads/MOSAIC_NYC_Sole_Source_Student_Stability_K8_OnePage.pdf',
    spec: '/api/blm-downloads/MOSAIC_Student_Stability_K8_Kit_Spec_Sheet.pdf',
  });

  // Optional: swap relative /api URLs for absolute backend URLs if needed.
  useEffect(() => {
    if (API) {
      setPdfUrls({
        letter: `${API}/api/blm-downloads/MOSAIC_NYC_Sole_Source_Student_Stability_K8_OnePage.pdf`,
        spec: `${API}/api/blm-downloads/MOSAIC_Student_Stability_K8_Kit_Spec_Sheet.pdf`,
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="stability-kits-page">
      {/* ─────────────── Hero ─────────────── */}
      <section className="border-b border-gray-200 bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-semibold tracking-wide uppercase" data-testid="mosaic-program-badge">
              <Check size={12} /> A MOSAIC Program
            </span>
            <span className="text-xs text-gray-500">At-home supplement to the MOSAIC language proficiency assessment platform</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Student Stability &amp; <span className="text-indigo-700">Engagement</span> System<sup className="text-xl">™</sup>
          </h1>
          <p className="mt-5 text-lg md:text-xl text-gray-700 max-w-3xl leading-relaxed">
            Physical at-home learning kits for <strong>all learners</strong>, K–8. 100% offline. No internet, device, or power required.
          </p>
          <p className="mt-3 text-sm text-gray-500 max-w-3xl">
            Exclusively published and distributed by Lerno Co. under the MOSAIC brand. NYC Vendor Code <strong>LER599869</strong>.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
            <Link to="/quote?focus=sses">
              <Button className="bg-indigo-700 hover:bg-indigo-800 text-white h-11 px-6" data-testid="cta-request-quote">
                Request a Quote <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            {/* v6.33 — Student-Stability sole-vendor letter withdrawn
                pending rewrite to Teacher Edition vocabulary. The current
                Teacher Edition sole-vendor letter covers the K-12 catalog
                and is linked from the Legal page and the Quote flow. */}
            <a href={pdfUrls.spec} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="h-11 px-5 border-gray-300" data-testid="cta-kit-spec-sheet">
                <FileText size={16} className="mr-2" /> Kit Spec Sheet (PDF)
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ─────────────── What's In Each Kit ─────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>What&rsquo;s inside every Student Kit</h2>
          <p className="mt-2 text-gray-600 max-w-2xl">Five purpose-built print modules that work together — indoors, outdoors, during transitions, or over the summer.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5" data-testid="modules-grid">
          {MODULES.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.name} className={`rounded-xl border border-gray-200 bg-gradient-to-br ${m.accent} p-6 ring-1 ${m.ring} hover:shadow-md transition-shadow`} data-testid={`module-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 w-11 h-11 rounded-lg ${m.iconBg} flex items-center justify-center`}>
                    <Icon size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900">{m.name}<sup className="text-xs">™</sup></h3>
                    <p className="mt-0.5 text-sm font-medium text-indigo-700 italic">{m.tagline}</p>
                    <p className="mt-3 text-sm leading-relaxed text-gray-700">{m.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ─────────────── Grade-Level Scale ─────────────── */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Grade-Level Scale — K through 8</h2>
          <p className="mt-2 text-gray-600 max-w-2xl">Content, reading level, and themes calibrated for each grade band.</p>

          <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm" data-testid="grade-scale-table">
              <thead className="bg-indigo-700 text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Grade Band</th>
                  <th className="text-left px-5 py-3 font-semibold">Reading Level Target</th>
                  <th className="text-left px-5 py-3 font-semibold">Focus Themes</th>
                </tr>
              </thead>
              <tbody>
                {GRADE_SCALE.map((r, i) => (
                  <tr key={r.band} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-5 py-3 font-semibold text-gray-900">{r.band}</td>
                    <td className="px-5 py-3 text-gray-700">{r.reading}</td>
                    <td className="px-5 py-3 text-gray-700">{r.themes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─────────────── TalkBridge Languages ─────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>TalkBridge<sup className="text-base">™</sup> — 10 bilingual family languages</h2>
        <p className="mt-2 text-gray-600 max-w-2xl">Printed family reply packets in the top NYCPS household languages — supporting Chancellor&rsquo;s Regulation A-663 language access.</p>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" data-testid="languages-grid">
          {LANGUAGES.map(([en, native]) => (
            <div key={en} className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
              <div className="text-sm font-bold text-indigo-700">{en}</div>
              <div className="text-xs text-gray-600 mt-0.5">{native}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────── Per-Grade SKUs + Pricing ─────────────── */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>One SKU per grade · Flat $149.00 per student</h2>
          <p className="mt-2 text-gray-600 max-w-2xl">Order exactly what each classroom needs — no bundled tiers, no volume discounts. Transparent, quote-ready pricing for NYC procurement.</p>

          <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm" data-testid="sku-pricing-table">
              <thead className="bg-indigo-700 text-white">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Grade</th>
                  <th className="text-left px-5 py-3 font-semibold">SKU</th>
                  <th className="text-left px-5 py-3 font-semibold">Unit</th>
                  <th className="text-right px-5 py-3 font-semibold">Price</th>
                </tr>
              </thead>
              <tbody>
                {GRADE_SKUS.map((r, i) => (
                  <tr key={r.sku} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-5 py-3 font-semibold text-gray-900">{r.grade}</td>
                    <td className="px-5 py-3 font-mono text-xs text-indigo-700">{r.sku}</td>
                    <td className="px-5 py-3 text-gray-700">per student</td>
                    <td className="px-5 py-3 text-right font-bold text-gray-900">$149.00</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/quote?focus=sses">
              <Button className="bg-indigo-700 hover:bg-indigo-800 text-white h-11 px-6" data-testid="cta-build-quote">
                Build a Quote in the Calculator <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="mailto:orders@mosaicassessmentco.com?subject=Student%20Stability%20Kits%20Inquiry" className="text-sm text-indigo-700 hover:underline font-medium inline-flex items-center gap-1">
              <Mail size={14} /> orders@mosaicassessmentco.com
            </a>
          </div>
        </div>
      </section>

      {/* ─────────────── Procurement & Compliance ─────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        <div className="rounded-2xl border border-gray-200 bg-white p-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Procurement-ready. Sole-source eligible.</h2>
          <p className="mt-3 text-gray-700 max-w-3xl leading-relaxed">
            StoryPath<sup>™</sup>, LifeMath<sup>™</sup>, TalkBridge<sup>™</sup>, CalmFocus<sup>™</sup>, and Offline Learning Quest<sup>™</sup> are trademarks of Lerno Co. All print content is original copyrighted material produced and distributed exclusively by Lerno Co. under the MOSAIC brand. The MOSAIC Student Stability &amp; Engagement System constitutes a <strong>sole commodity</strong> under NYC PPB Rules §3-05. Competitive bidding is inapplicable because these goods are available from only one source.
          </p>
          <div className="mt-6 grid md:grid-cols-2 gap-3">
            <a href={pdfUrls.letter} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors" data-testid="procurement-sole-vendor-letter">
              <div>
                <div className="font-semibold text-gray-900">One-Page Sole Vendor Letter</div>
                <div className="text-xs text-gray-500 mt-0.5">NYCPS Division of Contracts &amp; Purchasing · ready to submit</div>
              </div>
              <ExternalLink size={16} className="text-gray-400" />
            </a>
            <a href={pdfUrls.spec} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-5 py-4 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors" data-testid="procurement-kit-spec-sheet">
              <div>
                <div className="font-semibold text-gray-900">Kit Specification Sheet</div>
                <div className="text-xs text-gray-500 mt-0.5">What&rsquo;s inside every Student Kit — one page</div>
              </div>
              <ExternalLink size={16} className="text-gray-400" />
            </a>
          </div>
          <p className="mt-6 text-xs text-gray-500">
            WIDA<sup>®</sup> is a registered trademark of the Board of Regents of the University of Wisconsin System. MOSAIC is not affiliated with, endorsed by, or sponsored by WIDA.
          </p>
        </div>
      </section>
    </div>
  );
};

export default StabilityKits;
